import { Component, TableColumn, Viewport, type BreakpointOverride } from "@/types";
import type { ResponsiveBreakpoint } from "@/stores/editor/responsive-store";
import { getBreakpointFromViewport } from "@/utils/breakpoint-utils";

export type TableColumnBreakpoint = "mobile" | "tablet" | "desktop";

export const TABLE_COLUMN_BREAKPOINTS: Array<{
  id: TableColumnBreakpoint;
  label: string;
}> = [
  { id: "mobile", label: "Mobile" },
  { id: "tablet", label: "Tablet" },
  { id: "desktop", label: "Desktop" },
];

export const TABLE_VISIBLE_COLUMN_IDS_PROPERTY = "tableVisibleColumnIds";
export const TABLE_VISIBLE_COLUMN_IDS_BY_BREAKPOINT_PROPERTY =
  "tableVisibleColumnIdsByBreakpoint";

const metadataFields = new Set([
  "_id",
  "__v",
  "subRows",
  "schemaId",
  "schemaVersion",
  "groupId",
  "organizationId",
  "metadata",
  "createdAt",
  "updatedAt",
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isTableColumnLike = (value: unknown): value is TableColumn =>
  isRecord(value) &&
  typeof value.accessorKey === "string" &&
  typeof value.header === "string" &&
  (value.id === undefined || typeof value.id === "string");

const readStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;

  const strings = value.filter((item): item is string => typeof item === "string");
  return strings.length === value.length ? strings : null;
};

const formatColumnHeader = (key: string): string =>
  key
    .replace(/^(avg|sum|count|min|max|none)_/, "")
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
    .trim();

export const getTableColumnKey = (column: TableColumn): string =>
  column.id || column.accessorKey;

export const getBreakpointFromTableViewport = (
  viewport?: Viewport | ResponsiveBreakpoint | string,
): TableColumnBreakpoint | undefined => {
  return viewport === undefined ? undefined : getBreakpointFromViewport(viewport);
};

export const getBreakpointFromWidth = (width: number): TableColumnBreakpoint =>
  width < 768 ? "mobile" : width < 1024 ? "tablet" : "desktop";

export const getConfigurableTableColumns = (
  properties: Record<string, unknown>,
): TableColumn[] => {
  const configuredColumns = Array.isArray(properties.columns)
    ? properties.columns.filter(isTableColumnLike)
    : [];

  if (configuredColumns.length > 0) {
    return configuredColumns.map((column) => ({
      ...column,
      id: column.id || column.accessorKey,
    }));
  }

  const rows = Array.isArray(properties.data)
    ? properties.data.filter(isRecord)
    : [];
  const firstRow = rows[0];
  if (!firstRow) return [];

  return Object.keys(firstRow)
    .filter((key) => !metadataFields.has(key))
    .filter((key) => {
      const value = firstRow[key];
      return (
        value === null ||
        value === undefined ||
        typeof value !== "object" ||
        value instanceof Date ||
        Array.isArray(value)
      );
    })
    .map((key) => ({
      id: key,
      accessorKey: key,
      header: formatColumnHeader(key),
      enableSorting: true,
      enableColumnFilter: true,
    }));
};

export const getVisibleColumnIdsFromProperties = (
  properties: Record<string, unknown>,
): string[] | null =>
  readStringArray(properties[TABLE_VISIBLE_COLUMN_IDS_PROPERTY]);

export const getVisibleColumnIdsByBreakpointFromProperties = (
  properties: Record<string, unknown>,
  breakpoint: TableColumnBreakpoint,
): string[] | null => {
  const visibilityByBreakpoint =
    properties[TABLE_VISIBLE_COLUMN_IDS_BY_BREAKPOINT_PROPERTY];
  if (!isRecord(visibilityByBreakpoint)) return null;

  return readStringArray(visibilityByBreakpoint[breakpoint]);
};

export const getResponsiveVisibleColumnIds = (
  component: Component,
  breakpoint: TableColumnBreakpoint,
): string[] | null => {
  const breakpointProperties =
    component.responsivePosition?.breakpoints?.[breakpoint]?.properties;
  return breakpointProperties
    ? getVisibleColumnIdsFromProperties(breakpointProperties)
    : null;
};

export const getVisibleTableColumns = (
  columns: TableColumn[],
  properties: Record<string, unknown>,
): TableColumn[] => {
  const visibleColumnIds = getVisibleColumnIdsFromProperties(properties);
  if (!visibleColumnIds) return columns;

  const visibleColumnIdSet = new Set(visibleColumnIds);
  return columns.filter((column) =>
    visibleColumnIdSet.has(getTableColumnKey(column)),
  );
};

export const applyTableColumnVisibilityForBreakpoint = (
  properties: Record<string, unknown>,
  breakpoint: TableColumnBreakpoint | undefined,
): Record<string, unknown> => {
  if (!breakpoint) return properties;

  const visibleColumnIds = getVisibleColumnIdsByBreakpointFromProperties(
    properties,
    breakpoint,
  );
  if (!visibleColumnIds) return properties;

  return {
    ...properties,
    [TABLE_VISIBLE_COLUMN_IDS_PROPERTY]: visibleColumnIds,
  };
};

export const createResponsiveColumnVisibilityUpdate = (
  component: Component,
  breakpoint: TableColumnBreakpoint,
  visibleColumnIds: string[],
): Pick<Component, "responsivePosition" | "properties"> => {
  const existingResponsivePosition = component.responsivePosition;
  const existingBreakpoints = existingResponsivePosition?.breakpoints ?? {};
  const existingBreakpoint: BreakpointOverride = existingBreakpoints[breakpoint] ?? {};
  const existingProperties = existingBreakpoint.properties ?? {};
  const existingVisibilityByBreakpoint = isRecord(
    component.properties[TABLE_VISIBLE_COLUMN_IDS_BY_BREAKPOINT_PROPERTY],
  )
    ? component.properties[TABLE_VISIBLE_COLUMN_IDS_BY_BREAKPOINT_PROPERTY]
    : {};

  return {
    properties: {
      [TABLE_VISIBLE_COLUMN_IDS_BY_BREAKPOINT_PROPERTY]: {
        ...existingVisibilityByBreakpoint,
        [breakpoint]: visibleColumnIds,
      },
    },
    responsivePosition: {
      ...existingResponsivePosition,
      strategy: existingResponsivePosition?.strategy ?? "auto",
      breakpoints: {
        [breakpoint]: {
          ...existingBreakpoint,
          properties: {
            ...existingProperties,
            [TABLE_VISIBLE_COLUMN_IDS_PROPERTY]: visibleColumnIds,
          },
        },
      },
    },
  };
};
